/**
 * Anchor navigation config: DOM ids for scroll targets + scroll-spy, cluster
 * grouping, and the sticky/scroll-margin offsets shared between the anchor bar
 * and the sections it targets.
 */

import { css } from '@bts-web/utils-style-engine';

export type AnchorClusterKey = 'key_facts' | 'research' | 'activity';

/** DOM ids applied to the anchored section roots. Present only when a section
 * actually renders (self-hiding sections return null), so an id in the DOM is a
 * reliable "this section exists" signal for both scroll targets and scroll-spy. */
export const ANCHOR_IDS = {
  keyFacts: 'anchor-key-facts',
  researchAbout: 'anchor-research-about',
  researchStocksEtf: 'anchor-research-stocks-etf',
  researchCompanyFinancials: 'anchor-research-company-financial',
  activityActions: 'anchor-activity-actions',
  activityOrders: 'anchor-activity-orders',
  activityStaking: 'anchor-activity-staking',
  activityTransactions: 'anchor-activity-transactions',
} as const;

/** Cluster -> anchor ids in DOM order. The scroll target for a pill is the first
 * id in its list that is present in the DOM. */
export const CLUSTER_IDS: Record<AnchorClusterKey, string[]> = {
  key_facts: [ANCHOR_IDS.keyFacts],
  research: [
    ANCHOR_IDS.researchAbout,
    ANCHOR_IDS.researchStocksEtf,
    ANCHOR_IDS.researchCompanyFinancials,
  ],
  activity: [
    ANCHOR_IDS.activityActions,
    ANCHOR_IDS.activityOrders,
    ANCHOR_IDS.activityStaking,
  ],
};

/** Which anchor id belongs to which cluster (reverse lookup for scroll-spy). */
export const ID_TO_CLUSTER: Record<string, AnchorClusterKey> = Object.entries(
  CLUSTER_IDS,
).reduce<Record<string, AnchorClusterKey>>((acc, [cluster, ids]) => {
  ids.forEach((id) => {
    acc[id] = cluster as AnchorClusterKey;
  });

  return acc;
}, {});

/**
 * Sticky offsets (px). Mobile stacks a back-nav header (~52px) above the anchor
 * bar; desktop has no back-nav, only the global 71px fixed header.
 */
export const HEADER_HEIGHT = { base: 52, lg: 71 };

/** Anchor bar sticks directly below the header (mobile) / global header (desktop). */
export const ANCHOR_BAR_TOP = {
  base: HEADER_HEIGHT.base,
  lg: HEADER_HEIGHT.lg,
};

/** scroll-margin so scrollIntoView lands the section below header + bar. */
export const SCROLL_MARGIN_TOP = { base: 112, lg: 128 };

/** applied to anchored section roots so scrollIntoView clears the sticky
 * header + bar. Literals mirror SCROLL_MARGIN_TOP for PandaCSS static extraction. */
export const anchorScrollMarginClass = css({
  scrollMarginTop: { base: '112px', lg: '128px' },
});
